import type { Theme } from './themeTokens';

export const defaultThemes: Theme[] = [
  {
    id: 'padrao',
    name: 'Padrão IX Brasil',
    description: 'Tema padrão com identidade visual da IX Brasil Seguros',
    active: true,
    isDefault: true,
    colors: {
      primary: '#14B8A6',
      secondary: '#0F172A',
      accent: '#F59E0B',
      background: '#FFFFFF',
      surface: '#F8FAFC',
      text: '#0F172A',
      mutedText: '#64748B',
      border: '#E2E8F0',
      success: '#10B981',
      warning: '#F59E0B',
      error: '#EF4444',
      info: '#0EA5E9',
    },
    typography: {
      headingFont: "'Inter', sans-serif",
      bodyFont: "'Inter', sans-serif",
      baseSize: '16px',
      headingWeight: '700',
      bodyWeight: '400',
    },
    radius: { sm: '6px', md: '10px', lg: '14px', xl: '20px', full: '9999px' },
    shadow: {
      card: '0 4px 6px -1px rgba(0,0,0,0.1)',
      button: '0 4px 12px rgba(20,184,166,0.3)',
      input: '0 1px 3px rgba(0,0,0,0.1)',
      dropdown: '0 10px 15px -3px rgba(0,0,0,0.1)',
      modal: '0 25px 50px -12px rgba(0,0,0,0.25)',
    },
    spacing: { xs: '8px', sm: '12px', md: '16px', lg: '24px', xl: '32px' },
    buttons: { variant: 'solid', textTransform: 'none', fontWeight: '600', paddingX: '24px', paddingY: '12px' },
    cards: { background: '#FFFFFF', border: '#E2E8F0', shadow: '0 4px 6px -1px rgba(0,0,0,0.1)', padding: '24px' },
    inputs: { background: '#FFFFFF', border: '#E2E8F0', focusBorder: '#14B8A6', padding: '12px 16px', height: '48px' },
  },
  {
    id: 'corporativo',
    name: 'Corporativo',
    description: 'Azul petróleo, verde petróleo, verde e laranja — identidade corporativa forte',
    active: false,
    isDefault: true,
    colors: {
      primary: '#1A5276',    // Azul petróleo
      secondary: '#0E6655',  // Verde petróleo
      accent: '#E67E22',     // Laranja
      background: '#FFFFFF',
      surface: '#F4F6F7',
      text: '#1C2833',
      mutedText: '#6B7280',
      border: '#D5D8DC',
      success: '#27AE60',    // Verde
      warning: '#E67E22',    // Laranja
      error: '#C0392B',
      info: '#1A5276',       // Azul petróleo
    },
    typography: {
      headingFont: "'Inter', sans-serif",
      bodyFont: "'Inter', sans-serif",
      baseSize: '16px',
      headingWeight: '700',
      bodyWeight: '400',
    },
    radius: { sm: '4px', md: '8px', lg: '12px', xl: '16px', full: '9999px' },
    shadow: {
      card: '0 2px 8px rgba(26,82,118,0.10)',
      button: '0 4px 14px rgba(26,82,118,0.30)',
      input: '0 1px 3px rgba(0,0,0,0.08)',
      dropdown: '0 8px 20px rgba(0,0,0,0.12)',
      modal: '0 20px 40px rgba(0,0,0,0.20)',
    },
    spacing: { xs: '8px', sm: '12px', md: '16px', lg: '24px', xl: '32px' },
    buttons: { variant: 'solid', textTransform: 'none', fontWeight: '600', paddingX: '24px', paddingY: '12px' },
    cards: { background: '#FFFFFF', border: '#D5D8DC', shadow: '0 2px 8px rgba(26,82,118,0.10)', padding: '24px' },
    inputs: { background: '#FFFFFF', border: '#D5D8DC', focusBorder: '#1A5276', padding: '12px 16px', height: '48px' },
  },
  {
    id: 'premium',
    name: 'Premium',
    description: 'Preto profundo e dourado — exclusividade e sofisticação',
    active: false,
    isDefault: true,
    colors: {
      primary: '#1A1A2E',    // Preto azulado profundo
      secondary: '#16213E',  // Preto secundário
      accent: '#D4A017',     // Dourado
      background: '#FFFFFF',
      surface: '#FAFAF8',
      text: '#1A1A2E',
      mutedText: '#6B7280',
      border: '#E8E4DC',
      success: '#2D6A4F',
      warning: '#D4A017',
      error: '#C0392B',
      info: '#2C3E50',
    },
    typography: {
      headingFont: "'Playfair Display', serif",
      bodyFont: "'Inter', sans-serif",
      baseSize: '16px',
      headingWeight: '700',
      bodyWeight: '400',
    },
    radius: { sm: '4px', md: '6px', lg: '10px', xl: '14px', full: '9999px' },
    shadow: {
      card: '0 8px 24px rgba(26,26,46,0.10)',
      button: '0 4px 16px rgba(212,160,23,0.35)',
      input: '0 1px 3px rgba(0,0,0,0.10)',
      dropdown: '0 12px 28px rgba(0,0,0,0.15)',
      modal: '0 30px 60px rgba(0,0,0,0.25)',
    },
    spacing: { xs: '8px', sm: '12px', md: '16px', lg: '24px', xl: '32px' },
    buttons: { variant: 'solid', textTransform: 'uppercase', fontWeight: '700', paddingX: '28px', paddingY: '14px' },
    cards: { background: '#FFFFFF', border: '#E8E4DC', shadow: '0 8px 24px rgba(26,26,46,0.10)', padding: '28px' },
    inputs: { background: '#FFFFFF', border: '#E8E4DC', focusBorder: '#D4A017', padding: '12px 16px', height: '48px' },
  },
  {
    id: 'escuro',
    name: 'Escuro',
    description: 'Dark mode elegante com teal e âmbar — para ambientes com pouca luz',
    active: false,
    isDefault: true,
    colors: {
      primary: '#14B8A6',    // Teal
      secondary: '#94A3B8',  // Cinza azulado
      accent: '#F59E0B',     // Âmbar
      background: '#0F172A', // Fundo escuro
      surface: '#1E293B',    // Superfície escura
      text: '#F1F5F9',
      mutedText: '#94A3B8',
      border: '#334155',
      success: '#10B981',
      warning: '#F59E0B',
      error: '#EF4444',
      info: '#38BDF8',
    },
    typography: {
      headingFont: "'Inter', sans-serif",
      bodyFont: "'Inter', sans-serif",
      baseSize: '16px',
      headingWeight: '700',
      bodyWeight: '400',
    },
    radius: { sm: '6px', md: '10px', lg: '14px', xl: '20px', full: '9999px' },
    shadow: {
      card: '0 4px 16px rgba(0,0,0,0.40)',
      button: '0 4px 14px rgba(20,184,166,0.40)',
      input: '0 1px 4px rgba(0,0,0,0.30)',
      dropdown: '0 10px 24px rgba(0,0,0,0.50)',
      modal: '0 25px 50px rgba(0,0,0,0.60)',
    },
    spacing: { xs: '8px', sm: '12px', md: '16px', lg: '24px', xl: '32px' },
    buttons: { variant: 'solid', textTransform: 'none', fontWeight: '600', paddingX: '24px', paddingY: '12px' },
    cards: { background: '#1E293B', border: '#334155', shadow: '0 4px 16px rgba(0,0,0,0.40)', padding: '24px' },
    inputs: { background: '#1E293B', border: '#334155', focusBorder: '#14B8A6', padding: '12px 16px', height: '48px' },
  },
  {
    id: 'minimalista',
    name: 'Minimalista',
    description: 'Preto e verde — clean, direto ao ponto, foco total no conteúdo',
    active: false,
    isDefault: true,
    colors: {
      primary: '#18181B',    // Preto quase puro
      secondary: '#3F3F46',  // Cinza escuro
      accent: '#16A34A',     // Verde
      background: '#FFFFFF',
      surface: '#FAFAFA',
      text: '#18181B',
      mutedText: '#71717A',
      border: '#E4E4E7',
      success: '#16A34A',
      warning: '#CA8A04',
      error: '#DC2626',
      info: '#2563EB',
    },
    typography: {
      headingFont: "'Inter', sans-serif",
      bodyFont: "'Inter', sans-serif",
      baseSize: '15px',
      headingWeight: '600',
      bodyWeight: '400',
    },
    radius: { sm: '3px', md: '5px', lg: '8px', xl: '12px', full: '9999px' },
    shadow: {
      card: '0 1px 4px rgba(0,0,0,0.06)',
      button: '0 1px 4px rgba(0,0,0,0.12)',
      input: '0 1px 2px rgba(0,0,0,0.06)',
      dropdown: '0 6px 14px rgba(0,0,0,0.08)',
      modal: '0 20px 40px rgba(0,0,0,0.15)',
    },
    spacing: { xs: '8px', sm: '12px', md: '16px', lg: '24px', xl: '32px' },
    buttons: { variant: 'outline', textTransform: 'none', fontWeight: '500', paddingX: '20px', paddingY: '10px' },
    cards: { background: '#FFFFFF', border: '#E4E4E7', shadow: '0 1px 4px rgba(0,0,0,0.06)', padding: '20px' },
    inputs: { background: '#FFFFFF', border: '#E4E4E7', focusBorder: '#18181B', padding: '10px 14px', height: '44px' },
  },
  {
    id: 'moderno',
    name: 'Moderno',
    description: 'Verde vibrante e laranja — energia, inovação e ousadia',
    active: false,
    isDefault: true,
    colors: {
      primary: '#059669',    // Verde esmeralda
      secondary: '#065F46',  // Verde escuro
      accent: '#F97316',     // Laranja vibrante
      background: '#FFFFFF',
      surface: '#F0FDF4',
      text: '#064E3B',
      mutedText: '#6B7280',
      border: '#A7F3D0',
      success: '#059669',
      warning: '#F97316',
      error: '#EF4444',
      info: '#0EA5E9',
    },
    typography: {
      headingFont: "'Poppins', sans-serif",
      bodyFont: "'Inter', sans-serif",
      baseSize: '16px',
      headingWeight: '700',
      bodyWeight: '400',
    },
    radius: { sm: '8px', md: '14px', lg: '20px', xl: '28px', full: '9999px' },
    shadow: {
      card: '0 8px 30px rgba(5,150,105,0.10)',
      button: '0 6px 20px rgba(5,150,105,0.35)',
      input: '0 1px 3px rgba(0,0,0,0.08)',
      dropdown: '0 10px 24px rgba(0,0,0,0.10)',
      modal: '0 25px 50px rgba(0,0,0,0.20)',
    },
    spacing: { xs: '8px', sm: '12px', md: '16px', lg: '24px', xl: '32px' },
    buttons: { variant: 'solid', textTransform: 'none', fontWeight: '600', paddingX: '24px', paddingY: '12px' },
    cards: { background: '#FFFFFF', border: '#A7F3D0', shadow: '0 8px 30px rgba(5,150,105,0.10)', padding: '24px' },
    inputs: { background: '#FFFFFF', border: '#A7F3D0', focusBorder: '#059669', padding: '12px 16px', height: '48px' },
  },
];
